import { TCurrency } from '@qapline/core';
import { T } from '@qapline/translations';
import { Currency } from 'modules/common';
import z from 'zod';

// Re-export core payment/invoice enums so the admin app has one source of truth
export type { TExtendedInvoice, TInvoicePaymentStatus, TInvoiceStatus } from '@qapline/core';
export { invoicePaymentStatusEnum } from '@qapline/core';

// Legacy PDF preview endpoint schema (PDF endpoint still expects paymentMethod)
const zodVatCustomValidator = (value: number) => {
    if (value !== 0 && value !== 21) {
        throw new Error('Invalid value. Only 0 and 21 are allowed.');
    }
    return value;
};

const commissionToPdfSchema = () =>
    z.object({
        text: z.string(),
        orderedBy: z.string(),
        orderDate: z.string(),
        loadingDate: z.string(),
        price: z.number(),
        vat: z.number().refine(zodVatCustomValidator),
    });

const invoicePreviewPdfRequestSchema = () =>
    z.object({
        createdBy: z.string(),
        createdByMail: z.string().email(),
        customerCompany: z.string(),
        customerStreet: z.string(),
        customerCountry: z.string(),
        customerPostalCode: z.string(),
        customerCity: z.string(),
        registrationNumber: z.string(),
        currency: z.string(),
        taxId: z.string(),
        language: z.enum(['cs', 'en', 'de']),
        rateBase: z.number(),
        varSymbol: z.string(),
        constSymbol: z.string(),
        orderDate: z.string(),
        exposureDate: z.string(),
        maturityDate: z.string(),
        performanceDate: z.string(),
        commissions: z.array(commissionToPdfSchema()),
        paymentMethod: z.string(),
    });

// Form row for a commission attached to an invoice (display + editable VAT)
const invoiceFormCommissionSchema = () =>
    z.object({
        commission_id: z.number(),
        commissionNumber: z.string().optional().nullable(),
        qid: z.string().optional().nullable(),
        vat: z.string(),
        priceCustomer: z.number(),
        currencyCustomer: z.nativeEnum(Currency),
        customer_company: z.string(),
        orderDate: z.string(),
        pickupDate: z.number().nullish(),
        pointDate: z.number().nullish(),
        loading_city_string: z.string(),
        discharge_city_string: z.string(),
        loading_date: z.array(z.number()),
        discharge_date: z.array(z.number()),
    });

// Basic fields on the invoice form (new API field names)
const basicInvoiceFormSchema = (t: T) =>
    z.object({
        invoice_id: z.number().optional(),
        // paid state
        paid: z.boolean(),
        paidAmount: z.string().nullable(),
        paymentStatus: z.enum(['PAID', 'UNPAID', 'OVERPAID', 'UNDERPAID']).optional(),
        paymentConfirmationDate: z.string().nullable(),
        paidStateChangedBy: z
            .object({
                user_id: z.number(),
                name: z.string(),
                surname: z.string(),
                email: z.string(),
            })
            .nullable(),
        paidStateChangedBy_id: z.number().nullable(),
        // state flags (derived from timestamps)
        exported: z.boolean(),
        // basic identifying data
        number: z.string(),
        customer_company: z
            .string({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.stringFormat'),
            })
            .min(1, { message: t('form.errors.required') }),
        currency: z
            .nativeEnum(Currency, {
                required_error: t('form.errors.required'),
            })
            .nullable(),
        // dates (strings in form state, converted at submit)
        datePoint: z
            .string({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.stringFormat'),
            })
            .min(1, { message: t('form.errors.required') }),
        dateOrder: z
            .string({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.stringFormat'),
            })
            .optional(),
        dateIssue: z
            .string({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.stringFormat'),
            })
            .min(1, { message: t('form.errors.required') }),
        dateDue: z
            .string({
                required_error: t('form.errors.required'),
                invalid_type_error: t('form.errors.stringFormat'),
            })
            .min(1, { message: t('form.errors.required') }),
        // order
        constantSymbol: z
            .string({
                invalid_type_error: t('form.errors.stringFormat'),
            })
            .nullable()
            .optional(),
        language: z.enum(['cs', 'en', 'de']),
        // properties, not used in the form
        companyRegistrationNumber: z.string().nullable(),
        customer_id: z.number().nullable(),
        deleted: z.boolean().optional(),
        totalCommissions: z.number().optional(),
        invoiceAttachmentId: z.number().nullable(),
    });

// basic + commissions array
export const invoiceFormSchema = (t: T) =>
    basicInvoiceFormSchema(t).extend({
        commissions: z.array(invoiceFormCommissionSchema()),
    });

export type InvoicePreviewPdfRequestSchema = z.infer<ReturnType<typeof invoicePreviewPdfRequestSchema>>;
export type BasicInvoiceFormSchema = z.infer<ReturnType<typeof basicInvoiceFormSchema>>;
export type InvoiceFormCommissionSchema = z.infer<ReturnType<typeof invoiceFormCommissionSchema>>;
export type InvoiceFormSchema = z.infer<ReturnType<typeof invoiceFormSchema>>;

// Currency re-export used in form callers
export type { TCurrency };

export type TInvoicingStatusItem = {
    customerId: number;
    customerCompany: string;
    czkCount: number;
    eurCount: number;
};

type TCommissionForInvoicingLocation = {
    city: string | null;
    country: string | null;
    countryCode: string | null;
    postalCode: string | null;
    street: string | null;
    latitude: number | null;
    longitude: number | null;
};

export type TCommissionForInvoicing = {
    commission_id: number;
    number: number | null;
    qid: string | null;
    orderDate: string | null;
    vat: number | null;
    customerPrice: number | null;
    customerCurrency: Currency | null;
    customerCurrencyExchangeRate: number | null;
    customer: {
        customer_id: number;
        name: string;
    } | null;
    loadings: { dateFrom: string | null; location: TCommissionForInvoicingLocation | null }[];
    discharges: { dateFrom: string | null; location: TCommissionForInvoicingLocation | null }[];
};

export * from './api';
